// Same window the board's placement overlay uses for its own optimistic writes.
const RECENT_WRITE_TTL_MS = 10_000

// A written row, or null for a deleted one (a tombstone).
export type RecentWrite<W> = { row: W | null; at: number }

/**
 * Lays this client's recent writes over hits read from a search index that lags writes.
 * Prunes `writes` in place: expired ones, and ones the index has caught up with.
 * Returns undefined when no write is left to apply.
 */
export function mergeRecentWrites<
  T extends { id: string; updatedDate: string },
  W extends { updatedDate?: string | null },
>(hits: readonly T[], writes: Map<string, RecentWrite<W>>, adopt: (row: W) => T, now = Date.now()): T[] | undefined {
  for (const [id, write] of writes) {
    if (now - write.at > RECENT_WRITE_TTL_MS) writes.delete(id)
  }
  if (writes.size === 0) return undefined

  const merged = [...hits]
  for (const [id, write] of writes) {
    const index = merged.findIndex((item) => item.id === id)
    if (write.row === null) {
      if (index !== -1) merged.splice(index, 1)
    } else if (index !== -1 && Date.parse(merged[index].updatedDate) > Date.parse(write.row.updatedDate ?? ``)) {
      writes.delete(id)
    } else if (index === -1) {
      merged.unshift(adopt(write.row))
    } else {
      merged[index] = adopt(write.row)
    }
  }
  return merged
}
